import type { VerificationIdentityData, VerificationResult } from '@/types/verification';
import { Button } from '@/components/ui/Button';
import { Download } from 'lucide-react';

function imageSrc(image?: string) {
  if (!image) return null;
  if (image.startsWith('data:')) return image;
  return `data:image/jpeg;base64,${image}`;
}

function fullName(data: VerificationIdentityData) {
  const parts = [data.firstName, data.middleName, data.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : 'N/A';
}

interface VerificationResultPanelProps {
  result: VerificationResult;
  onDownload?: () => void;
  downloading?: boolean;
}

export function VerificationResultPanel({
  result,
  onDownload,
  downloading = false,
}: VerificationResultPanelProps) {
  const data = result.data;
  const photo = imageSrc(data.image);
  const isAdvancedBvn = result.slipType === 'advanced_slip' && result.verificationType === 'BVN';

  const basicRows: { label: string; value?: string }[] = [
    { label: 'NIN', value: data.nin },
    { label: 'BVN', value: data.bvn },
    { label: 'Full Name', value: fullName(data) },
    { label: 'Gender', value: data.gender },
    { label: 'Date of Birth', value: data.dateOfBirth },
    { label: 'Phone', value: data.phoneNumber1 },
    { label: 'Tracking ID', value: data.trackingId },
    { label: 'Address', value: data.residenceAddress || data.residentialAddress },
  ].filter((row) => row.value);

  const advancedRows: { label: string; value?: string }[] = [
    { label: 'Name on Card', value: data.nameOnCard },
    { label: 'Phone Number 2', value: data.phoneNumber2 },
    { label: 'State of Origin', value: data.stateOfOrigin },
    { label: 'LGA of Origin', value: data.lgaOfOrigin },
    { label: 'State of Residence', value: data.stateOfResidence },
    { label: 'LGA of Residence', value: data.lgaOfResidence },
    { label: 'Registration Date', value: data.registrationDate },
    { label: 'Enrollment Bank', value: data.enrollmentBank },
    { label: 'Enrollment Branch', value: data.enrollmentBranch },
    { label: 'Level of Account', value: data.levelOfAccount },
    { label: 'Marital Status', value: data.maritalStatus },
    { label: 'Title', value: data.title },
    { label: 'Linked NIN', value: data.nin },
    { label: 'Watch Listed', value: data.watchListed },
  ].filter((row) => row.value);

  const rows = isAdvancedBvn ? [...basicRows, ...advancedRows] : basicRows;

  return (
    <div className="mx-auto w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
      <div className="bg-gradient-to-r from-brand-green to-emerald-600 px-6 py-4 text-center text-white">
        <h3 className="text-xl font-bold">Verification Result</h3>
        <p className="mt-1 text-sm text-white/90">
          {result.verificationType} · {result.slipType.replace(/_/g, ' ')}
        </p>
      </div>

      <div className="space-y-4 p-6">
        {photo && (
          <div className="flex justify-center">
            <img
              src={photo}
              alt="Passport"
              className="h-40 w-32 rounded-lg border border-gray-200 object-cover"
            />
          </div>
        )}

        <dl className="divide-y divide-gray-100">
          {rows.map((row) => (
            <div key={row.label} className="grid gap-1 py-3 sm:grid-cols-3">
              <dt className="text-sm font-semibold text-gray-600">{row.label}</dt>
              <dd className="text-sm text-gray-900 sm:col-span-2">{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <p>Reference: {result.reference}</p>
          <p>Amount paid: ₦{result.amountPaid.toFixed(2)}</p>
          {result.dateGenerated && <p>Generated: {new Date(result.dateGenerated).toLocaleString()}</p>}
        </div>

        {onDownload && (
          <Button type="button" fullWidth onClick={onDownload} loading={downloading} loadingText="Generating PDF...">
            <Download className="mr-2 h-4 w-4" />
            Download slip PDF
          </Button>
        )}
      </div>
    </div>
  );
}
